import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { groupsApi } from "../api/groups";

const groupKeys = {
  all: ["groups"] as const,
  list: () => [...groupKeys.all, "list"] as const,
  detail: (id: string) => [...groupKeys.all, "detail", id] as const,
  children: (id: string) => [...groupKeys.all, "children", id] as const,
  members: (id: string, params?: Record<string, unknown>) =>
    [...groupKeys.all, "members", id, params] as const,
};

export function useGroupsList() {
  return useQuery({
    queryKey: groupKeys.list(),
    queryFn: () => groupsApi.list(),
    staleTime: 30_000,
  });
}

export function useGroup(id: string) {
  return useQuery({
    queryKey: groupKeys.detail(id),
    queryFn: () => groupsApi.get(id),
    enabled: !!id,
  });
}

export function useGroupChildren(id: string) {
  return useQuery({
    queryKey: groupKeys.children(id),
    queryFn: () => groupsApi.getChildren(id),
    enabled: !!id,
  });
}

export function useGroupMembers(id: string, params?: { first?: number; max?: number }) {
  return useQuery({
    queryKey: groupKeys.members(id, params as Record<string, unknown>),
    queryFn: () => groupsApi.getMembers(id, params),
    enabled: !!id,
    staleTime: 15_000,
  });
}

export function useAddGroupMember(groupId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => groupsApi.addMember(groupId, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: groupKeys.members(groupId) });
      qc.invalidateQueries({ queryKey: ["users"] });
    },
  });
}

export function useRemoveGroupMember(groupId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => groupsApi.removeMember(groupId, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: groupKeys.members(groupId) });
      qc.invalidateQueries({ queryKey: ["users"] });
    },
  });
}
